/**
 * Unsynced work on the user's other devices (Phase 16C C10, design §2.1). Laptops and second
 * devices ask the server, on mount and every 60 s while online, which of the user's enrolled
 * phones or tablets hold unsynced work. Ids only; nothing else is fetched.
 */
import { useEffect, useState } from 'react';
import { DeviceService } from '../services/offline/deviceService';
import { DeviceContext } from '../services/offline/deviceContext';
import { useOnlineStatus } from './useOfflineDevice';
import type { PendingDeviceDto } from '../dtos/offline/offline.dto';

const POLL_MS = 60_000;

export interface UnsyncedDevices {
  devices: PendingDeviceDto[];
  visitIds: Set<string>;
  patientIds: Set<string>;
  any: boolean;
}

export function useUnsyncedDevices(enabled = true): UnsyncedDevices {
  const online = useOnlineStatus();
  const [devices, setDevices] = useState<PendingDeviceDto[]>([]);

  useEffect(() => {
    if (!enabled || !online) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const own = DeviceContext.enrolledDeviceId();
        const list = (await DeviceService.pending()).filter((d) => d.deviceId !== own && d.pendingOps > 0);
        if (!cancelled) setDevices(list);
      } catch {
        // optional hint: keep the last answer
      }
    };
    load();
    const timer = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled, online]);

  return {
    devices,
    visitIds: new Set(devices.flatMap((d) => d.visitIds || [])),
    patientIds: new Set(devices.flatMap((d) => d.patientIds || [])),
    any: devices.length > 0,
  };
}
